const SWATCH_INPUT_SELECTOR =
  '.variant-option__button-label--has-swatch input[data-option-available]';
const UNAVAILABLE_SWATCH_SELECTOR = `${SWATCH_INPUT_SELECTOR}[data-option-available='false']`;
const MANAGED_ATTRIBUTE = 'data-atelea-unavailable-disabled';
const REVEAL_SELECTOR = '[data-atelea-reveal]';
const REVEAL_READY_ATTRIBUTE = 'data-atelea-reveal-ready';
const REVEAL_VISIBLE_ATTRIBUTE = 'data-atelea-reveal';
const PHOTO_FRAME_SELECTOR = [
  '.resource-card__media',
  '.product-media-container',
  '.media-block',
  '.background-image-container',
  '.comparison-slider__media-wrapper',
  '.resource-image',
  '.atelea-product-hotspots__content',
  '.atelea-color-story__media',
  '.atelea-home-community__media',
  '.atelea-about__media',
  '.atelea-choice-media__frame',
  '.atelea-email-popup__media',
  '.atelea-pdp-editorial__media',
  '.atelea-pdp-pack__visual',
  '.atelea-collections__media',
  '.atelea-bundle-builder__preview-media',
].join(', ');
const PHOTO_LOADING_ATTRIBUTE = 'data-atelea-photo-loading';
const PHOTO_STATE_ATTRIBUTE = 'data-atelea-photo-state';
const PHOTO_ERROR_ATTRIBUTE = 'data-atelea-photo-error';
const FOCUS_MODALITY_ATTRIBUTE = 'data-atelea-focus-modality';
const KEYBOARD_FOCUS_KEYS = new Set([
  'Tab',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Home',
]);

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @type {IntersectionObserver | null} */
let photoObserver = null;

/** @param {Element} element */
function revealElement(element) {
  element.setAttribute(REVEAL_VISIBLE_ATTRIBUTE, 'visible');
}

/** @param {Document | Element} root */
function observeAteleaReveals(root = document) {
  const elements =
    root instanceof Element && root.matches(REVEAL_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(REVEAL_SELECTOR)];

  if (!elements.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(revealElement);
    return;
  }

  document.documentElement.setAttribute(REVEAL_READY_ATTRIBUTE, 'true');

  if (!window.IntersectionObserver) {
    elements.forEach(revealElement);
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !(entry.target instanceof Element)) return;
        revealElement(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );

  elements.forEach((element) => {
    if (element.getAttribute(REVEAL_VISIBLE_ATTRIBUTE) !== 'visible') {
      revealObserver?.observe(element);
    }
  });
}

/** @param {HTMLImageElement} image */
function getAteleaPhotoFrame(image) {
  if (
    image.matches('.resource-card__image--secondary, .hero__media') ||
    image.closest('.hero, .hero__media-wrapper')
  ) {
    return null;
  }

  const navigationPhoto = image.matches(
    '.mega-menu__link-image, .menu-drawer__link-image'
  );
  const frame = image.closest(PHOTO_FRAME_SELECTOR) ??
    (navigationPhoto ? image.closest('a') : null);
  if (!frame) return null;
  if (
    frame.matches('.atelea-product-hotspots__content') &&
    !image.matches('.hotspots__background-image')
  ) {
    return null;
  }

  return frame;
}

/** @param {Element} frame */
function isAteleaPhotoFrameLargeEnough(frame) {
  const bounds = frame.getBoundingClientRect();
  return (
    bounds.width === 0 ||
    bounds.height === 0 ||
    (bounds.width >= 120 && bounds.height >= 96)
  );
}

/** @param {HTMLImageElement} image */
function getAteleaPhotoSourceKey(image) {
  return [
    image.getAttribute('src') || '',
    image.getAttribute('srcset') || '',
    image.getAttribute('sizes') || '',
    image.currentSrc,
  ].join('|');
}

/** @param {HTMLImageElement} image @param {string} sourceKey */
async function finishAteleaPhotoAfterDecode(image, sourceKey) {
  if (typeof image.decode === 'function') {
    try {
      await image.decode();
    } catch {}
  }

  if (sourceKey === getAteleaPhotoSourceKey(image)) finishAteleaPhoto(image);
}

/** @param {HTMLImageElement} image */
function animateAteleaPhoto(image) {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    typeof image.animate !== 'function'
  ) {
    return;
  }

  const opacity = window.getComputedStyle(image).opacity;
  image.animate([{ opacity: 0 }, { opacity }], {
    duration: 240,
    easing: 'ease-out',
  });
}

/** @param {HTMLImageElement} image */
function finishAteleaPhoto(image) {
  const frame = getAteleaPhotoFrame(image);
  const loaded = image.naturalWidth > 0;
  if (loaded) {
    image.removeAttribute(PHOTO_STATE_ATTRIBUTE);
    frame?.removeAttribute(PHOTO_ERROR_ATTRIBUTE);
    animateAteleaPhoto(image);
  } else {
    image.removeAttribute(PHOTO_STATE_ATTRIBUTE);
    if (frame?.matches('.atelea-product-hotspots__content')) {
      frame.setAttribute(PHOTO_ERROR_ATTRIBUTE, 'true');
      setHotspotPhotoLayer(frame, true);
    }
  }

  if (frame && !frame.querySelector(`img[${PHOTO_STATE_ATTRIBUTE}='loading']`)) {
    frame.removeAttribute(PHOTO_LOADING_ATTRIBUTE);
    if (loaded) setHotspotPhotoLayer(frame, false);
  }
}

/** @param {Element} frame @param {boolean} loading */
function setHotspotPhotoLayer(frame, loading) {
  if (!frame.matches('.atelea-product-hotspots__content')) return;

  const layer = frame.querySelector('.hotspots-container');
  if (!(layer instanceof HTMLElement)) return;

  layer.toggleAttribute('inert', loading);
  if (loading) {
    layer.setAttribute('aria-hidden', 'true');
  } else {
    layer.removeAttribute('aria-hidden');
  }
}

/** @param {HTMLImageElement} image */
function reobserveAteleaPhotoSource(image) {
  const frame = getAteleaPhotoFrame(image);
  if (!frame || !isAteleaPhotoFrameLargeEnough(frame)) return;

  photoObserver?.unobserve(image);
  frame.removeAttribute(PHOTO_ERROR_ATTRIBUTE);
  setHotspotPhotoLayer(frame, true);
  if (image.complete && image.naturalWidth === 0) {
    finishAteleaPhoto(image);
    return;
  }

  photoObserver?.observe(image);
}

/** @param {Document | Element} root */
function observeAteleaPhotoLoads(root = document) {
  const images =
    root instanceof Element && root.matches('img')
      ? [root]
      : [...root.querySelectorAll('img')];

  if (!images.length) return;

  photoObserver ??= window.IntersectionObserver
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || !(entry.target instanceof HTMLImageElement)) return;

            const image = entry.target;
            const frame = getAteleaPhotoFrame(image);
            observer.unobserve(image);
            if (!frame) return;
            if (image.complete && image.naturalWidth === 0) {
              finishAteleaPhoto(image);
              return;
            }

            frame.setAttribute(PHOTO_LOADING_ATTRIBUTE, 'true');
            image.setAttribute(PHOTO_STATE_ATTRIBUTE, 'loading');
            setHotspotPhotoLayer(frame, true);
            if (image.complete) {
              void finishAteleaPhotoAfterDecode(image, getAteleaPhotoSourceKey(image));
            }
          });
        },
        { rootMargin: '240px 0px' }
      )
    : null;

  images.forEach((image) => {
    if (!(image instanceof HTMLImageElement)) return;
    const frame = getAteleaPhotoFrame(image);
    if (!frame || !isAteleaPhotoFrameLargeEnough(frame)) return;

    if (!image.hasAttribute('data-atelea-photo-managed')) {
      image.setAttribute('data-atelea-photo-managed', 'true');
      image.addEventListener('load', () => {
        if (image.getAttribute(PHOTO_STATE_ATTRIBUTE) !== 'loading') return;
        void finishAteleaPhotoAfterDecode(image, getAteleaPhotoSourceKey(image));
      });
      image.addEventListener('error', () => {
        if (
          image.getAttribute(PHOTO_STATE_ATTRIBUTE) === 'loading' ||
          getAteleaPhotoFrame(image)?.matches('.atelea-product-hotspots__content')
        ) {
          finishAteleaPhoto(image);
        }
      });
    }

    if (image.complete && image.naturalWidth === 0) {
      finishAteleaPhoto(image);
      return;
    }
    photoObserver?.observe(image);
  });
}

document.addEventListener(
  'pointerdown',
  () => document.documentElement.setAttribute(FOCUS_MODALITY_ATTRIBUTE, 'pointer'),
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    if (KEYBOARD_FOCUS_KEYS.has(event.key)) {
      document.documentElement.setAttribute(FOCUS_MODALITY_ATTRIBUTE, 'keyboard');
    }
  },
  true
);

/** @param {ParentNode} root */
function syncUnavailableSwatches(root) {
  const inputs = root instanceof HTMLInputElement && root.matches(SWATCH_INPUT_SELECTOR)
    ? [root]
    : root.querySelectorAll(SWATCH_INPUT_SELECTOR);

  for (const input of inputs) {
    if (!(input instanceof HTMLInputElement)) continue;

    const unavailable = input.matches(UNAVAILABLE_SWATCH_SELECTOR);
    if (unavailable) {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
      input.setAttribute(MANAGED_ATTRIBUTE, 'true');
      continue;
    }

    if (input.getAttribute(MANAGED_ATTRIBUTE) !== 'true') continue;
    input.disabled = false;
    input.removeAttribute('aria-disabled');
    input.removeAttribute(MANAGED_ATTRIBUTE);
  }
}

syncUnavailableSwatches(document);
observeAteleaReveals();
observeAteleaPhotoLoads();

const unavailableSwatchObserver = new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes') {
      const target = record.target;
      if (
        target instanceof HTMLImageElement &&
        ['src', 'srcset', 'sizes'].includes(record.attributeName || '')
      ) {
        reobserveAteleaPhotoSource(target);
        continue;
      }
      if (target instanceof HTMLInputElement) {
        syncUnavailableSwatches(target.parentElement || document);
      }
      continue;
    }

    for (const node of record.addedNodes) {
      if (node instanceof Element) {
        syncUnavailableSwatches(node);
        observeAteleaPhotoLoads(node);
      }
    }
  }
});

if (document.body) {
  unavailableSwatchObserver.observe(document.body, {
    attributeFilter: ['data-option-available', 'sizes', 'src', 'srcset'],
    attributes: true,
    childList: true,
    subtree: true,
  });
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  syncUnavailableSwatches(event.target);
  observeAteleaReveals(event.target);
  observeAteleaPhotoLoads(event.target);
});
